import React, { useEffect, useState, useRef, useContext } from "react";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import "./settings.module.css";
import { PermissionMenuContext } from "../../contextApi/permissionBasedMenuContext";
import NavigationTabs from "../../globalComponents/ui/NavigationTabs";
import Profile from "./Profile";
import Billing from "./Billing";
import SubUsers from "./SubUsers";
import Generator from "./Generator";
import AccessManagement from "./AccessManagement";
import LocationManagement from "./Location";
import OrgChartFromAuth from "../org-chart/OrgChartFromAuth";
import DesignationManagement from "./DesignationManagement";
import QuestionFrequency from "./QuestionFrequency";
import Unit from "./Unit";
import Emission from "./Emission";
import Triggers from "./Triggers";
import GHGProtocol from "./GHGProtocol";
import EmailNotifications from "./EmailNotifications";
import LockQuestion from "./LockQuestion";
import EmissionKPI from "./EmissionKPI";
import GHGDbKPISettings from "./GHGDbSettingByKpi";
import OwnershipControl from "./OwnershipControl";
import KpisGroup from "./KpisGroup";
import KPIAssetSettings from "./KpiAssets";
import IntensitySettings from "./Intensity";
import NetZeroSettings from "./NetZero";
import AuthorizationSettings from "./AuthorizationSettings";
import Training from "./Training";

import { usePermission } from "../../hooks/usePermission";

const TAB_CONFIG = {
  "Profile Management": { label: "Profile", icon: "user", component: (perms) => <Profile userPermissionList={perms} tab="profile" /> },
  // "Billing Management": { label: "Billing", icon: "credit-card", component: (perms, companyId) => companyId && companyId !== "345" ? <Billing tab="billing" userPermissionList={perms} /> : null },
  // "Lock Question": { label: "Lock question", icon: "lock", component: () => <LockQuestion /> },
  "User Management": { label: "User", icon: "users", component: (perms) => <SubUsers tab="subaccounts" userPermissionList={perms} /> },
  // "Designation Management": { label: "Designation", icon: "briefcase", component: (perms) => <DesignationManagement tab="designation" userPermissionList={perms} /> },
  "Location Management": { label: "Location", icon: "map-pin", component: (perms) => <LocationManagement tab="location" userPermissionList={perms} /> },
  "Permission Management": { label: "Permission", icon: "shield", component: (perms) => <AccessManagement tab="permission" userPermissionList={perms} /> },
  "Create Org Chart": { label: "Chart", icon: "chart-area", component: (perms) => <OrgChartFromAuth tab="chart" userPermissionList={perms} /> },
  "Question Frequency": { label: "Frequency", icon: "clock", component: (perms) => <QuestionFrequency tab="frequency" userPermissionList={perms} /> },
  // "GHG Setup": { label: "GHG setup", icon: "leaf", component: (perms) => <GHGProtocol tab="gwp" userPermissionList={perms} /> },
  "Email Notifications": { label: "Email notifications", icon: "bell", component: () => <EmailNotifications /> },
  "Emission": { label: "Emission", icon: "flame", component: () => <Emission /> },
  "Trigger": { label: "Trigger", icon: "bolt", component: () => <Triggers /> },
  "KPIs": { label: "KPIs", icon: "chart-bar", component: () => <KpisGroup /> },
  "Ownership Control": { label: "Ownership control", icon: "key", component: () => <OwnershipControl /> },
  "Intensity": { label: "Intensity", icon: "bolt", component: () => <IntensitySettings /> },
  "Net Zero": { label: "Net Zero", icon: "leaf", component: () => <NetZeroSettings /> },
  "Authorization": { label: "Authorization Control", icon: "shield", component: () => <AuthorizationSettings /> },
  "Training": { label: "Training", icon: "graduation-cap", component: () => <Training /> }
};

const Settings = (props) => {
  const context = useContext(PermissionMenuContext);
  const { hasPermission } = usePermission();
  const [menuList, setMenuList] = useState([]);
  const [activeCaption, setActiveCaption] = useState(null);
  const isMounted = useRef(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const handleSidebarToggle = (isOpen) => setSidebarExpanded(isOpen);

  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const companyId = currentUser.company_id;

  useEffect(() => {
    const settingsMenu = JSON.parse(localStorage.getItem("menu")) || [];
    const settingsObject = settingsMenu.find((item) => item.caption === "Settings");
    const settingsSubMenu = settingsObject ? settingsObject.sub_menu : [];
    if (isMounted.current) {
      setMenuList(settingsSubMenu);
      if (settingsSubMenu.length > 0 && !activeCaption) {
        setActiveCaption(settingsSubMenu[0].caption);
      }
    }
  }, [context.state]);

  const ALWAYS_SHOWN_TABS = ["Intensity", "Net Zero", "Training"];

  const visibleTabs = [
    ...menuList
      .filter((item) => {
        if (!TAB_CONFIG[item.caption]) return false;
        if (
          item.caption === "Question Frequency" &&
          !["351", "362"].includes(String(companyId))
        ) {
          return false;
        }

        // Double-gate settings sub-tabs using capability rules
        if (item.caption === "Profile Management") return true;
        if (item.caption === "User Management") {
          return hasPermission("user-management.invite", "INVITE") ||
            hasPermission("user-management.update", "EDIT") ||
            hasPermission("user-management.update", "UPDATE_SUB_USER");
        }
        if (item.caption === "Location Management") {
          return hasPermission("location-management.view", "VIEW") ||
            hasPermission("location-management.create", "CREATE") ||
            hasPermission("location-management.update", "EDIT") ||
            hasPermission("location-management.delete", "DELETE");
        }
        if (item.caption === "Permission Management") {
          return hasPermission("permission-management.create-management-role", "CREATE_MANAGEMENT_ROLE") ||
            hasPermission("permission-management.reassign-user", "REASSIGN_USER") ||
            hasPermission("permission-management.remove-user", "REMOVE_USER");
        }
        if (item.caption === "Create Org Chart") {
          return hasPermission("org-chart.view", "ORG_CHART");
        }
        if (item.caption === "Question Frequency") {
          return hasPermission("question-frequency.view", "QUESTION_FREQUENCY");
        }
        if (item.caption === "Email Notifications") {
          return hasPermission("notification.email", "NOTIFICATION");
        }
        if (item.caption === "Emission") {
          return hasPermission("emission.view", "EMISSION");
        }
        if (item.caption === "Trigger") {
          return hasPermission("trigger.view", "TRIGGER");
        }
        if (item.caption === "KPIs") {
          return (
            hasPermission("active-kpis.view", "KPIS") ||
            hasPermission("active-kpis.view", "KPI") ||
            hasPermission("unit.view", "UNIT") ||
            hasPermission("ghg-databases.view", "GHG_SETUP") ||
            hasPermission("ghg-databases.view", "GHG_DATABASES") ||
            hasPermission("energy-sources.view", "ENERGY_SOURCES") ||
            hasPermission("kpi-assets.view", "KPI_ASSETS") ||
            hasPermission("kpi-assets.view", "KPIS") ||
            hasPermission("kpi-required-evidences.view", "REQUIRED_EVIDENCES") ||
            hasPermission("kpi-required-evidences.view", "KPIS")
          );
        }
        if (item.caption === "Ownership Control") {
          return hasPermission("ownership-control.view", "OWNERSHIP_CONTROL");
        }
        if (item.caption === "Authorization") {
          return hasPermission("authorization.view", "AUTHORIZATION");
        }
        return true;
      })
      .map((item) => ({
        key: item.caption,
        label: TAB_CONFIG[item.caption].label,
        icon: TAB_CONFIG[item.caption].icon,
        permissions: item.permissions,
        component: TAB_CONFIG[item.caption].component,
      })),
    ...ALWAYS_SHOWN_TABS
      .filter((caption) => !menuList.some((item) => item.caption === caption))
      .map((caption) => ({ key: caption, label: TAB_CONFIG[caption].label, icon: TAB_CONFIG[caption].icon, permissions: [], component: TAB_CONFIG[caption].component })),
  ];



  const activeTab = visibleTabs.find((t) => t.key === activeCaption);

  const renderUI = () => {
    if (!activeTab) return null;
    return activeTab.component(activeTab.permissions, companyId) ?? null;
  };

  return (
    <PermissionMenuContext.Consumer>
      {() => (
        <div
          className="d-flex flex-row mainclass"
          style={{ height: "100vh", overflow: "auto" }}
        >
          <div
            style={{
              flex: sidebarExpanded ? "0 0 21%" : "0 0 60px",
              position: "sticky",
              top: 0,
              zIndex: 999,
              transition: "flex 0.3s ease",
            }}
          >
            <Sidebar
              dataFromParent={props.location.pathname}
              onSidebarToggle={handleSidebarToggle}
            />
          </div>

          <div
            style={{
              flex: sidebarExpanded ? "1 1 79%" : "1 1 calc(100% - 60px)",
              transition: "flex 0.3s ease",
              minHeight: "100vh",
              overflowY: "auto",
            }}
          >
            <div style={{ position: "sticky", top: 0, zIndex: 999 }}>
              <Header />
            </div>

            <div
              className="main_wrapper"
              style={{ width: sidebarExpanded ? "79vw" : "calc(100%)" }}
            >
              <div
                className="inner_wraapper px-3 pt-3 scroll-container w-100"
                style={{ width: "100%" }}
              >
                <NavigationTabs
                  tabs={visibleTabs}
                  activeKey={activeCaption}
                  onChange={setActiveCaption}
                />
                {renderUI()}
              </div>
            </div>
          </div>
        </div>
      )}
    </PermissionMenuContext.Consumer>
  );
};

export default Settings;